import React, { useState, useEffect } from 'react'
import { MdFastRewind, MdFastForward } from 'react-icons/md'
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  OverlayTrigger,
  Tooltip,
} from 'react-bootstrap'
import { useLocation } from 'react-router-dom'
import axios from 'axios'
import videojs from 'video.js'
import { MdKeyboardArrowUp, MdKeyboardArrowDown } from 'react-icons/md'
import 'video.js/dist/video-js.css'
import '@videojs/themes/dist/fantasy/index.css'

import ChatList from '../../components/ChatList'
import useQuery from '../../utils/location'
import vtranscript from '../../utils/videojs-transcript'

import '../../utils/videojs-transcript/videojs-transcript.css'
import './index.css'

const RecordingPlayer = (props) => {
  const location = useLocation()
  const query = useQuery(location)
  const source = query.get('source')
  const isGoogleSource = source === 'google'
  const [recording, setRecording] = useState({
    title: location.state?.title || '',
    link: location.state?.link || '',
    date: location.state?.createdAt || '',
    s3Key: location.state?.s3Key || '',
    s3Link: location.state?.s3Link || '',
    transcriptLink: location.state?.transcriptLink || '',
    chatLink: location.state?.chatLink || '',
  })

  const [videoRef, setVideoRef] = useState()
  const [videoPlayer, setVideoPlayer] = useState()
  const [loadCorrected, setLoadCorrected] = useState(false)
  const [isAuthorized, setAuthorized] = useState(true)
  const [isLoading, setLoading] = useState(false)
  const [isLoadingTranscription, setLoadingTranscription] = useState(false)
  const [isInvalidTranscription, setInvalidTranscription] = useState(false)
  const [googleAuthenticated, setGoogleAuthentication] = useState(false)
  const [searchResults, setSearchResults] = useState({
    matches: [],
    length: 0,
    currentIndex: 0,
  })
  const [seekBar, setSeekBar] = useState()
  const [searchKeyword, setSearchKeyword] = useState('')
  const [chat, setChat] = useState()

  const recordingId = props.match.params.recordingId

  const loadRecording = async (password) => {
    const response = await axios.get(
      `/recording?recordingId=${recordingId}${
        password ? `&password=${password}` : ''
      }`
    )
    return response.data
  }

  const initializeRecording = async (password) => {
    try {
      const response = await loadRecording(password)
      setRecording({
        title: response.title,
        link: response.link,
        date: response.createdAt,
        s3Key: response.s3Key,
        s3Link: response.s3Link,
        transcriptLink: response.transcriptLink,
        chatLink: response.chatLink,
      })
      setAuthorized(true)
      setLoading(false)
      setLoadingTranscription(true)
    } catch (e) {
      setLoading(false)
      if (!isGoogleSource) {
        if (e.response.status === 402) {
          setAuthorized(false)
        } else if (!window.localStorage.getItem('token')) {
          window.location.href = `/?source=${window.location.origin}/recording/${recordingId}`
        } else {
          window.location.href = `/courses`
        }
      } else {
        setGoogleAuthentication(false)
        alert('Invalid Password')
      }
    }
  }

  const checkTranscript = async () => {
    try {
      /**
       * Check if the transcript exists.
       */
      const transcription = await axios.get(recording.transcriptLink)
      if (!transcription.data.startsWith('WEBVTT')) {
        throw Error('Error')
      }

      videoPlayer.addRemoteTextTrack({
        default: 'showing',
        kind: 'captions',
        label: 'English',
        src: recording.transcriptLink,
        srclang: 'en',
      })

      const transcript = videoPlayer.transcript()
      const transcriptContainer = document.getElementById('transcript')
      transcriptContainer.appendChild(transcript.el())

      setTimeout(() => {
        setLoadingTranscription(false)
        transcriptContainer.style.visibility = 'visible'
        videoPlayer.textTracks().tracks_[0].mode = 'hidden'

        if (location && location.state && location.state.searchKey) {
          search(location.state.searchKey)
        }
      }, 2000)
    } catch (e) {
      console.log('Bad transcript', e)
      setLoadingTranscription(false)
      setInvalidTranscription(true)
    }
  }

  let searchTimeout
  const search = (keyword) => {
    setSearchKeyword(keyword)

    const execute = () => {
      const elements = new Array(
        ...document.getElementsByClassName('transcript-text')
      )

      let isFirstMatch = true
      const matches = elements.filter((element) => {
        const searchKey = new RegExp(keyword, 'i')

        if (keyword && searchKey.test(element.innerText)) {
          if (isFirstMatch) {
            element.classList.remove('match')
            element.classList.add('match-active')
            isFirstMatch = false
          } else {
            element.classList.add('match')
          }

          return true
        } else {
          element.classList.remove('match')
          element.classList.remove('match-active')

          return false
        }
      })

      if (matches && matches.length) {
        if (matches[0].scrollIntoViewIfNeeded) {
          matches[0].scrollIntoViewIfNeeded()
        } else {
          matches[0].scrollIntoView()
        }
      } else {
        const len = elements.length
        for (let i = 0; i < len; i++) {
          const element = elements[i]
          element.classList.remove('match')
          element.classList.remove('match-active')
        }
      }

      setSearchResults({
        matches,
        length: matches.length,
        currentIndex: 0,
      })
    }

    clearTimeout(searchTimeout)
    searchTimeout = setTimeout(execute, 100)
  }

  const checkChat = async () => {
    try {
      const response = await axios.get(recording.chatLink)
      const chatRaw = response.data
      if (!chatRaw && !chatRaw.length) return false

      const regex = /\d{2}:\d{2}:\d{2}(.+(\n(?!\d{2}:\d{2}:\d{2}))*)+/gm
      const chatLines = chatRaw.match(regex)
      const formatted = chatLines.map((str) => {
        const [time, sender, message] = str.split(/\t/)
  
        return { time, sender, message }
      })
      
      setChat(formatted)
    } catch (e) {
      console.log(e.message)
    }
  }

  const highlightMatch = (direction) => {
    const directions = {
      up: -1,
      down: 1,
    }

    setSearchResults(({ matches, length, currentIndex: prevIndex }) => {
      const currentIndex =
        (prevIndex === 0 && direction === 'up') ||
        (prevIndex + 1 === length && direction === 'down')
          ? prevIndex
          : prevIndex + directions[direction]

      const previousElement = matches[prevIndex]
      previousElement.classList.remove('match-active')
      previousElement.classList.add('match')

      const currentElement = matches[currentIndex]
      currentElement.classList.remove('match')
      currentElement.classList.add('match-active')

      if (currentElement.scrollIntoViewIfNeeded) {
        currentElement.scrollIntoViewIfNeeded()
      } else {
        currentElement.scrollIntoView()
      }

      return { matches, length, currentIndex }
    })
  }

  useEffect(() => {
    if (isGoogleSource) return

    setLoading(true)

    if (location.state && !location.state.searchKey && !location.state.from) {
      setRecording({ ...location.state })
      setLoading(false)
    } else {
      initializeRecording()
    }
  }, [])

  useEffect(() => {
    if (videoPlayer && recording) {
      checkTranscript()
      checkChat()
    }

    if (loadCorrected) {
      return
    }
    try {
      const player = videojs(
        videoRef,
        {
          preload: 'auto',
          autoplay: false,
          controls: true,
          responsive: true,
          userActions: {
            hotkeys: function (e) {
              e.preventDefault()
              e.stopPropagation()

              const controlBar = this.getChild('ControlBar')
              const playToggle = controlBar.getChild('PlayToggle')

              switch (e.code) {
                case 'Space':
                  playToggle.handleClick()
                  break
                case 'ArrowLeft':
                  seekBar.stepBack()
                  break
                case 'ArrowRight':
                  seekBar.stepForward()
                  break
                default:
                  console.log(e.code)
              }
            },
          },
          playbackRates: [0.5, 1, 1.5, 2],
          poster:
            'https://kingsland-resources.s3.us-east-2.amazonaws.com/cover.jpg',
          sources: [
            {
              src: recording.s3Link.replace(`%27`, `'`),
              type: 'video/mp4',
            },
          ],
        },
        () => {
          setLoadCorrected(true)
        }
      )

      vtranscript(window, videojs)
      setVideoPlayer(player)

      const controlBarDOM =
        document.getElementsByClassName('vjs-control-bar')[0]
      const volumePanel = document.getElementsByClassName('vjs-volume-panel')[0]
      const videoControls = document.getElementById('video-controls')
      videoControls.style.visibility = 'visible'

      controlBarDOM.insertBefore(videoControls, volumePanel)

      const controlBar = player.getChild('ControlBar')
      const progressControl = controlBar.getChild('ProgressControl')
      const seekBar = progressControl.getChild('SeekBar')
      const tts = player.getChild('TextTrackSettings')
      tts.options_.persistTextTrackSettings = true

      if (window.localStorage.getItem('vjs-text-track-settings')) {
        tts.restoreSettings()
      } else {
        tts.setValues({ backgroundOpacity: '0.5', fontPercent: 0.75 })
        tts.saveSettings()
      }

      setSeekBar(seekBar)
    } catch (e) {
      console.log('Element not loaded correctly, retrying...')
    }
  }, [loadCorrected, recording, videoRef])

  const authenticate = async (e) => {
    setGoogleAuthentication(true)
    setLoading(true)
    e.preventDefault()

    const password = e.target[0].value

    await initializeRecording(password)
  }

  const { length: searchResultsLen, currentIndex: searchResultsCurrIndex } =
    searchResults

  return (
    <Container className="recording-container">
      <div id="video-controls">
        <OverlayTrigger
          placement="bottom"
          overlay={<Tooltip>Rewind for 5 seconds</Tooltip>}
        >
          <MdFastRewind
            className="control-btn"
            onClick={() => seekBar.stepBack()}
            type="button"
            size={25}
          />
        </OverlayTrigger>
        <OverlayTrigger
          placement="bottom"
          overlay={<Tooltip>Seek for 5 seconds</Tooltip>}
        >
          <MdFastForward
            className="control-btn"
            onClick={() => seekBar.stepForward()}
            type="button"
            size={25}
          />
        </OverlayTrigger>
      </div>
      {isAuthorized ? (
        <>
          <Row>
            <p className="recording-title">{recording.title}</p>
          </Row>
          <Row className="justify-content-center">
            <Col md={4}>
              {isGoogleSource && !googleAuthenticated ? (
                <Card className="p-5">
                  <p>Please enter the recording password</p>
                  <p className="subtext">
                    The password for this recording can be found on Google
                    Classroom under the description of this class material.
                  </p>
                  <Form onSubmit={authenticate}>
                    <Form.Group controlId="videoPassword">
                      <Form.Control
                        className="mb-2"
                        type="password"
                        name="password"
                        placeholder="Password"
                      />
                    </Form.Group>
                    <div className="d-grid gap-2">
                      <Button
                        className="authenticate-button"
                        type="submit"
                        disabled={isLoading}
                      >
                        Authenticate
                      </Button>
                    </div>
                  </Form>
                </Card>
              ) : isLoading ? (
                'Retrieving recording, please wait...'
              ) : (
                <p className="recording-date">
                  Uploaded at: {new Date(recording.date).toLocaleString()}
                </p>
              )}
            </Col>
          </Row>
          <Row onContextMenu={(e) => e.preventDefault()}>
            {recording.link && !recording.s3Link && (
              <iframe
                className="recording-iframe"
                src={recording.link}
                title={recording.title}
                allowFullScreen
              ></iframe>
            )}
          </Row>
          <Row
            className="justify-content-center"
            key={recording.s3Link}
            onContextMenu={(e) => e.preventDefault()}
          >
            {recording.s3Link && (
              <>
                <div className="d-flex">
                  <video
                    ref={(e) => {
                      setVideoRef(e)
                    }}
                    className="video-js vjs-theme-fantasy recording-iframe"
                    id="my-video"
                  ></video>
                  <ChatList chat={chat} />
                </div>
                {isLoadingTranscription && 'Loading Transcriptions...'}
                {isInvalidTranscription && 'No Transcriptions Available.'}
                <div id="transcript">
                  <div id="searchContainer">
                    <Form.Control
                      type="text"
                      name="searchKeyword"
                      placeholder="Search Keyword"
                      onChange={(e) => search(e.target.value)}
                      value={searchKeyword}
                    />
                    <div id="matches">
                      {!!searchResultsLen &&
                        `${searchResultsCurrIndex + 1} / ${searchResultsLen}`}
                    </div>
                    <MdKeyboardArrowUp
                      type="button"
                      size={35}
                      onClick={() => highlightMatch('up')}
                    />
                    <MdKeyboardArrowDown
                      type="button"
                      size={35}
                      onClick={() => highlightMatch('down')}
                    />
                  </div>
                </div>
              </>
            )}
          </Row>
        </>
      ) : (
        <Row>
          <p className="recording-title">
            You are not authorized to view this recording.
          </p>
          <p className="recording-date">
            You may have been withdrawn from this class. If you think this is a
            mistake, reach out on #admin-questions channel on Slack.{' '}
            <a href="/courses">Back to home.</a>
          </p>
        </Row>
      )}
    </Container>
  )
}

export default RecordingPlayer
